import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { X } from 'lucide-react'
import {
  STARTUP_WINDOW_SANDBOX,
  buildStartupWindowDocument,
  parseStartupWindowMessage,
  isAllowedStartupWindowHref,
  findMiniAppScreen,
} from '@tfa/shared'
import { useStartupWindow } from '../hooks/useStartupWindow'
import { useTelegram } from '../hooks/useTelegram'
import { useOpenHref } from '../lib/Linkify'
import { api, type StartupWindowClaim } from '../services/api'
import { useOverlayPresence } from '../lib/overlayGate'

const HOST_BG = '#1a1530'

/**
 * Хост стартового окна: полноэкранный оверлей ниже сторис (zIndex 1900),
 * внутри — sandbox-iframe с документом из общего билдера. Кнопка закрытия
 * живёт ВНЕ iframe: клиент никогда не заперт в окне админа, даже если HTML
 * битый. Сообщения моста принимаются только от своего iframe.
 */
export function StartupWindowHost() {
  const { pending, clear } = useStartupWindow()
  if (!pending) return null
  return <StartupWindowView window={pending} onClose={clear} />
}

function StartupWindowView({ window: w, onClose }: { window: StartupWindowClaim; onClose: () => void }) {
  useOverlayPresence('startup-window')
  const navigate = useNavigate()
  const { impactHaptic } = useTelegram()
  const openHref = useOpenHref()
  const iframeRef = useRef<HTMLIFrameElement>(null)
  const [ready, setReady] = useState(false)
  const closedRef = useRef(false)

  const srcDoc = useMemo(
    () => buildStartupWindowDocument(w.html, w.vars, { parentOrigin: window.location.origin }),
    [w.html, w.vars],
  )

  const finish = (report: 'dismiss' | null) => {
    if (closedRef.current) return
    closedRef.current = true
    if (report === 'dismiss') api.dismissStartupWindow(w.impressionId).catch(() => {})
    onClose()
  }

  const close = () => { impactHaptic('light'); finish('dismiss') }

  useEffect(() => {
    const t = setTimeout(() => setReady(true), 1500)
    const onMessage = (e: MessageEvent) => {
      if (!iframeRef.current || e.source !== iframeRef.current.contentWindow) return
      const msg = parseStartupWindowMessage(e.data)
      if (!msg) return
      switch (msg.action) {
        case 'ready':
          setReady(true)
          return
        case 'close':
          finish('dismiss')
          return
        case 'link': {
          if (!isAllowedStartupWindowHref(msg.href)) return
          api.clickStartupWindow(w.impressionId, { action: 'link', target: msg.href }).catch(() => {})
          openHref(msg.href)
          finish(null)
          return
        }
        case 'screen': {
          const screen = findMiniAppScreen(msg.screen)
          if (!screen) return
          api.clickStartupWindow(w.impressionId, { action: 'screen', target: msg.screen }).catch(() => {})
          finish(null)
          navigate(screen.path)
          return
        }
      }
    }
    window.addEventListener('message', onMessage)
    return () => {
      clearTimeout(t)
      window.removeEventListener('message', onMessage)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [w.impressionId])

  return (
    <div
      role="dialog"
      aria-label="Стартовое окно"
      data-testid="modal-startup-window"
      style={{ position: 'fixed', inset: 0, zIndex: 1900, background: HOST_BG }}
    >
      <iframe
        ref={iframeRef}
        title="startup-window"
        sandbox={STARTUP_WINDOW_SANDBOX}
        srcDoc={srcDoc}
        referrerPolicy="no-referrer"
        style={{ border: 0, width: '100%', height: '100%', display: 'block', background: 'transparent', opacity: ready ? 1 : 0, transition: 'opacity 160ms' }}
      />
      <button
        type="button"
        onClick={close}
        aria-label="Закрыть"
        data-testid="startup-window-close"
        style={{
          position: 'absolute',
          top: 'calc(10px + env(safe-area-inset-top, 0px))',
          right: 12,
          width: 36,
          height: 36,
          borderRadius: 18,
          border: 'none',
          background: 'rgba(0,0,0,0.35)',
          color: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          zIndex: 2,
        }}
      >
        <X size={20} />
      </button>
      {w.isTest && (
        <div
          data-testid="startup-window-test-badge"
          style={{
            position: 'absolute',
            top: 'calc(16px + env(safe-area-inset-top, 0px))',
            left: 12,
            padding: '3px 8px',
            borderRadius: 8,
            background: '#FFD166',
            color: '#1a1530',
            fontSize: 11,
            fontWeight: 800,
            zIndex: 2,
          }}
        >
          Тестовый показ
        </div>
      )}
    </div>
  )
}
